import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Calculator } from "lucide-react";
import { useTradeJournal } from "../../context/TradeJournalContext";
import RoiMinimalChart from "./RoiMinimalChart";
import { useIsMobile } from "../../hooks/use-mobile";

/**
 * Save/load starting capital to localStorage as before.
 */
function saveStartingCapital(value: number) {
  localStorage.setItem("startingTradingCapital", value.toString());
}
function loadStartingCapital(): number {
  const val = localStorage.getItem("startingTradingCapital");
  return val ? parseFloat(val) : 100000;
}

/**
 * Get ROI history based on Behavior Journal P&L.
 * Expects entries sorted by date ASC.
 */
function getRoiHistoryFromBehavior(entries: any[], startingCapital: number) {
  let cumulativePnl = 0;
  const sortedEntries = [...entries].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );
  console.log("Sorted Behavior Entries for ROI calc:", sortedEntries);

  return sortedEntries.map((entry) => {
    cumulativePnl += entry.dailyPnL;
    const roi =
      startingCapital > 0 ? (cumulativePnl / startingCapital) * 100 : 0;
    return {
      date: entry.date,
      roi: Number(roi.toFixed(2)),
      cumulativePnl: cumulativePnl,
    };
  });
}

const RoiSection: React.FC = () => {
  const isMobile = useIsMobile();
  const { filteredBehaviorEntries: behaviorEntries } = useTradeJournal();
  const [startingCapital, setStartingCapital] = useState<number>(
    loadStartingCapital()
  );
  const [localInput, setLocalInput] = useState<string>(
    startingCapital.toString()
  );

  const cumulativePnl = behaviorEntries.reduce(
    (acc, e) => acc + (typeof e.dailyPnL === "number" ? e.dailyPnL : 0),
    0
  );
  const roi = startingCapital > 0 ? (cumulativePnl / startingCapital) * 100 : 0;
  const roiHistory = getRoiHistoryFromBehavior(
    behaviorEntries,
    startingCapital
  );

  let peak = startingCapital;
  const drawdownHistory = roiHistory.map((point, i) => {
    peak = Math.max(peak, startingCapital + (roiHistory[i].cumulativePnl ?? 0));
    const equity = startingCapital + (point.cumulativePnl ?? 0);
    const drawdown = peak > 0 ? ((equity - peak) / peak) * 100 : 0;
    return {
      date: point.date,
      drawdown: Number(drawdown.toFixed(2)),
    };
  });

  const historicalMaxDrawdown = drawdownHistory.length
    ? Math.min(...drawdownHistory.map((d) => d.drawdown))
    : 0;

  const currentCapital = startingCapital + cumulativePnl;

  const roiVals = roiHistory.map((d) => d.roi);
  const bestRoi = roiVals.length ? Math.max(...roiVals) : 0;
  const worstRoi = roiVals.length ? Math.min(...roiVals) : 0;

  useEffect(() => {
    saveStartingCapital(startingCapital);
  }, [startingCapital]);

  function handleInputCommit() {
    const parsed = Number(localInput);
    if (!isNaN(parsed) && parsed > 0) {
      setStartingCapital(parsed);
    }
  }

  const formatCurrency = (val: number) => "₹" + val.toLocaleString("en-IN");

  console.log("ROI History (Behavior):", roiHistory);
  console.log("Behavior Entries available:", behaviorEntries);
  console.log("Entries count:", behaviorEntries.length);
  console.log("Cumulative PNL (Behavior):", cumulativePnl);
  console.log("Starting Capital:", startingCapital);
  console.log("Calculated ROI:", roi);

  return (
    <Card className="chart-card bg-white/5 border border-white/10 mb-14 p-0 rounded-2xl overflow-hidden shadow-lg glass-morphism w-full max-w-6xl mx-auto min-h-[490px] flex flex-col">
      <CardHeader className="flex flex-row items-center gap-3 bg-transparent border-0 rounded-t-xl px-6 md:px-10 py-5 md:py-7">
        <Calculator className="h-6 w-6 md:h-7 md:w-7 text-emerald-400 shrink-0" />
        <CardTitle className="chart-title text-xl md:text-3xl font-bold tracking-tight text-white">
          ROI Calculator{" "}
          <span className="text-emerald-300 font-black ml-2 text-xs md:text-base">
            Live
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col md:flex-row gap-x-10 gap-y-8 px-4 md:px-12 py-4 md:py-8 pb-6 md:pb-10 items-stretch h-full grow">
        <div className="flex flex-col gap-5 md:gap-7 w-full md:min-w-[250px] md:max-w-[320px] md:flex-shrink-0">
          <div>
            <label
              htmlFor="startingCapital"
              className="text-gray-400 text-xs font-semibold mb-1 block"
            >
              Initial Capital
            </label>
            <div className="flex items-center gap-2">
              <span className="text-gray-400 font-medium text-lg">₹</span>
              <Input
                id="startingCapital"
                type="number"
                defaultValue={100000}
                min={0}
                step={100}
                className="w-full max-w-[170px] bg-white/10 border border-white/10 rounded-lg text-white placeholder:text-gray-400 px-4 py-2 text-xl font-mono focus:border-emerald-400 focus:ring-emerald-200 focus:ring-2 focus:outline-none text-center"
                value={localInput}
                onChange={(e) => setLocalInput(e.target.value)}
                onBlur={handleInputCommit}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleInputCommit();
                }}
              />
            </div>
            <span className="text-xs text-gray-500 mt-1">
              Base for ROI calculation
            </span>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex flex-col gap-2 shadow-inner">
            <div className="flex items-center justify-between text-xs text-gray-300">
              <span>Current Capital</span>
              <span className="font-semibold text-white">
                {formatCurrency(currentCapital)}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs text-gray-300">
              <span>Total PnL</span>
              <span
                className={
                  "font-semibold " +
                  (cumulativePnl >= 0 ? "text-emerald-400" : "text-red-400")
                }
              >
                {formatCurrency(cumulativePnl)}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs text-gray-300">
              <span>Best ROI</span>
              <span className="font-semibold text-emerald-300">
                {bestRoi?.toFixed(2)}%
              </span>
            </div>
            <div className="flex items-center justify-between text-xs text-gray-300">
              <span>Worst ROI</span>
              <span className="font-semibold text-red-400">
                {worstRoi?.toFixed(2)}%
              </span>
            </div>
            <div className="flex items-center justify-between text-xs text-gray-300">
              <span>Historical Max Drawdown</span>
              <span className="font-semibold text-red-400">
                {historicalMaxDrawdown.toFixed(2)}%
              </span>
            </div>
          </div>

          {isMobile && (
            <div className="flex flex-col items-center justify-center py-3 px-4 mt-2">
              <div className="text-xs text-gray-300 uppercase font-bold mb-1 tracking-widest text-center">
                Current ROI
              </div>
              <div
                className={
                  "text-4xl md:text-7xl font-extrabold tracking-tight font-mono " +
                  (roi > 0
                    ? "text-emerald-400"
                    : roi < 0
                      ? "text-red-400"
                      : "text-gray-400")
                }
              >
                {Number.isFinite(roi) ? roi.toFixed(2) : "--"}
                <span className="text-xl align-super font-bold">%</span>
              </div>
              <div className="mt-1 text-xs text-gray-400 tracking-wide text-center">
                PnL:{" "}
                <span className="font-bold text-gray-200">
                  {formatCurrency(Math.abs(cumulativePnl))}
                </span>
              </div>
            </div>
          )}

          {!isMobile && (
            <div className="hidden md:flex flex-col items-center min-w-[260px] justify-center py-3 px-4 grow">
              <div className="text-xs text-gray-300 uppercase font-bold mb-1 tracking-widest text-center">
                Current ROI
              </div>
              <div
                className={
                  "text-6xl md:text-7xl font-extrabold tracking-tight font-mono " +
                  (roi > 0
                    ? "text-emerald-400"
                    : roi < 0
                      ? "text-red-400"
                      : "text-gray-400")
                }
              >
                {Number.isFinite(roi) ? roi.toFixed(2) : "--"}
                <span className="text-2xl align-super font-bold">%</span>
              </div>
              <div className="mt-3 text-xs text-gray-400 tracking-wide text-center">
                PnL:{" "}
                <span className="font-bold text-gray-200">
                  {formatCurrency(Math.abs(cumulativePnl))}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col justify-between w-full md:min-w-[420px] md:grow md:max-w-2xl h-auto md:h-full">
          <div className="bg-card rounded-xl border border-white/10 shadow-inner p-2 md:p-4 h-[240px] md:h-[300px] flex items-center">
            <RoiMinimalChart
              data={
                roiHistory.length > 0
                  ? roiHistory.map((p, i) => ({
                    ...p,
                    isLatest: i === roiHistory.length - 1,
                  }))
                  : []
              }
              drawdownData={drawdownHistory}
            />
          </div>

          <div className="text-xs text-gray-500 mt-2 px-1 md:pl-2 flex items-center flex-wrap gap-x-4 gap-y-1 justify-center md:justify-start">
            <div className="flex items-center gap-2">
              <div className="w-4 h-1 bg-emerald-400"></div>
              <span>ROI over time</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-1 border-b-2 border-orange-400 border-dashed"></div>
              <span>Drawdown over time</span>
            </div>
            <span>(Last point = latest entry)</span>
          </div>
        </div>
      </CardContent>
      <div className="bg-black/20 px-3 md:px-4 py-2 flex items-center gap-2 text-[10px] md:text-xs text-gray-400">
        <span>
          The ROI calculator uses only "Daily P&L" values from the Behavior
          Journal (Add Today's Journal), not Trade Journal.
        </span>
      </div>
    </Card>
  );
};

export default RoiSection;
